import React, { useContext, useEffect, useState } from "react";
import moment from "moment-timezone";
import { fromPairs } from "lodash";

import { InlineNotification } from "carbon-components-react";
import HeaderButton from "web_modules/capital_markets_dashboard/shared/HeaderButton";
import { nameToTitle } from "web_modules/capital_markets_dashboard/shared/utils";

import { rateAdjusterToRank, LoanPrograms } from "@eave/shared/types/api";
import { RatesContext } from "../RatesProvider";

import EditsPresentAlert from "./EditsPresentAlert";
import RatesTable from "./RatesTable";
import ValidationErrorsAlert from "./ValidationErrorsAlert";
import FicoLtvMatrix from "./FicoLtvMatrix";
import LtvAdjusters from "./LTVAdjusters";

const FontAwesome = require("react-fontawesome");

const styles = require("./styles.module.scss");

const latestTimestamp = (...ts: string[]): string => {
  let latest = null;
  ts.forEach(t => {
    if (latest === null || moment(t).isAfter(latest)) {
      latest = t;
    }
  });
  return latest;
};

const baseRatesRank = {
  [LoanPrograms.FIVE_ARM]: 1,
  [LoanPrograms.SEVEN_ARM]: 2,
  [LoanPrograms.TEN_ARM]: 3,
  [LoanPrograms.THIRTY_YEAR]: 4,
};

const RatesEditor = () => {
  const {
    loanType,
    updatedLoanType,
    editLoanType,
    busy,
    currentRates,
    updatedRates,
    currentAdjusters,
    updatedAdjusters,
    hasEdits,
    validationErrors,
    editRate,
    editAdjuster,
    editMatrix,
    undoEdits,
    saveEdits,
    refreshRatesForLoanType,
    refreshRateStatus,
  } = useContext(RatesContext);
  const [editing, setEditing] = useState<boolean>(false);

  useEffect(() => {
    setEditing(false);
  }, [busy]);

  const toggleEditing = () => {
    if (editing) {
      undoEdits();
    }
    setEditing(!editing);
  };

  const hasErrors = !!validationErrors.length;

  let alert;
  if (hasErrors) {
    alert = <ValidationErrorsAlert errors={validationErrors} />;
  } else if (hasEdits) {
    alert = <EditsPresentAlert name={loanType.name} />;
  }

  return (
    <>
      <div className={styles.header}>
        <h1 className={styles.title}>{nameToTitle(loanType.name)} pricing</h1>
        <HeaderButton className={styles.edit} onClick={toggleEditing}>
          <FontAwesome name={editing ? "close" : "pencil"} fixedWidth />{" "}
          {editing ? "Cancel" : "Edit"}
        </HeaderButton>
        <HeaderButton onClick={saveEdits} disabled={busy || hasErrors || !hasEdits}>
          <FontAwesome name="check" fixedWidth /> Save {nameToTitle(loanType.name)} pricing changes
        </HeaderButton>
        <HeaderButton
          onClick={refreshRatesForLoanType}
          disabled={
            busy ||
            (!!refreshRateStatus &&
              refreshRateStatus !== "Successfully updated rates from Optimal Blue.")
          }
        >
          <FontAwesome name="refresh" fixedWidth /> Refresh rates for {nameToTitle(loanType.name)}
        </HeaderButton>
      </div>
      {refreshRateStatus && (
        <InlineNotification
          lowContrast
          kind="info"
          notificationType="inline"
          role="alert"
          title={refreshRateStatus}
        />
      )}
      {alert}
      <RatesTable
        title={`${nameToTitle(loanType.name)} target price`}
        lastUpdated={loanType.updated_at}
        currentRates={{
          "Target price": loanType.target_price,
          "Target purchase price": loanType.target_purchase_price,
          "Target loan amount": loanType.target_loan_amount,
          "Target FICO": loanType.target_fico,
        }}
        updatedRates={{
          "Target price": updatedLoanType.target_price,
          "Target purchase price": updatedLoanType.target_purchase_price,
          "Target loan amount": updatedLoanType.target_loan_amount,
          "Target FICO": updatedLoanType.target_fico,
        }}
        editing={editing}
        onEdit={editLoanType}
        sortFunc={() => {}}
        suffix=""
        precision={{
          "Target price": 3,
          "Target purchase price": 0,
          "Target loan amount": 0,
          "Target FICO": 0,
        }}
      />
      <RatesTable
        title={`${nameToTitle(loanType.name)} base rates`}
        lastUpdated={latestTimestamp(...currentRates.map(r => r.created_at))}
        currentRates={fromPairs(currentRates.map(r => [r.loan_program_name, r.rate]))}
        updatedRates={fromPairs(updatedRates.map(r => [r.loan_program_name, r.rate]))}
        editing={editing}
        onEdit={editRate}
        sortFunc={r => baseRatesRank[r]}
      />
      <FicoLtvMatrix
        loanTypeName={loanType.name}
        editing={editing}
        onEdit={editMatrix}
        updatedAdjusters={updatedAdjusters}
        currentAdjusters={currentAdjusters}
      />
      {currentAdjusters.ltv_adjusters && Object.keys(currentAdjusters.ltv_adjusters).length > 0 ? (
        <LtvAdjusters loanTypeName={loanType.name} editing={editing} />
      ) : null}
      <RatesTable
        title={`${nameToTitle(loanType.name)} rate adjusters (LLPAs)`}
        lastUpdated={currentAdjusters.created_at}
        currentRates={currentAdjusters.adjusters}
        updatedRates={updatedAdjusters.adjusters}
        editing={editing}
        onEdit={editAdjuster}
        sortFunc={r => rateAdjusterToRank(r)}
      />
    </>
  );
};

export default RatesEditor;
